<script setup>
import { ref } from 'vue';
import { addDays, isToday, differenceInDays } from 'date-fns';
import { format } from '@/utils/date-fns';
import { capitalize } from 'lodash';
import i18n from '@/utils/i18n';

const emit = defineEmits(['date-selected']);

const selectedIndex = ref(0);

const props = defineProps({
  initialDate: {
    type: Date,
    default: () => {
      return new Date();
    },
  },
  color: {
    type: String,
    default: '',
  },
  endDate: {
    type: Date,
    default: () => {},
  },
  unavailableDates: {
    type: Array,
    default: () => [],
  },
});

function getIsToday(index) {
  return isToday(addDays(props.initialDate, index), new Date());
}

function getFormattedDate(index) {
  if (getIsToday(index)) return i18n.global.t('global.today');
  const date = capitalize(
    format(addDays(props.initialDate, index), 'EEEE dd MMMM')
  );
  return date.split(' ');
}

function dateSelected(index) {
  if (isUnavailable(index)) return;
  selectedIndex.value = index;
  emit('date-selected', index);
}

function daysAhead() {
  if (!props.endDate) return 30;
  return differenceInDays(new Date(props.endDate), props.initialDate) + 1;
}

function isUnavailable(index) {
  const today = new Date(props.initialDate);
  return props.unavailableDates.includes(
    format(addDays(today, index), 'yyyy-MM-dd')
  );
}

function getStyles(index) {
  const baseStyles = {
    width: '134px',
    height: '160px',
    minWidth: '134px',
    minHeight: '160px',
  };

  if (isUnavailable(index)) {
    return {
      ...baseStyles,
      opacity: '0.5',
    };
  }

  return baseStyles;
}
</script>

<template>
  <div class="">
    <p class="h800 mb-4">
      {{ props.endDate ? $t('global.endDate') : $t('global.startDate') }}
    </p>

    <div class="relative overflow-x-auto" style="width: calc(100vw - 110px)">
      <div class="flex gap-3">
        <div
          v-for="(_, index) in daysAhead()"
          :key="index"
          :class="[
            selectedIndex === index
              ? 'bg-darkOcean-100 border-4 border-darkOcean-600'
              : 'bg-neutral-0 border-1 border-neutral-500',
          ]"
          data-pressable
          class="flex flex-col items-center justify-center mr-3 rounded-[16px] border border-sm relative"
          :style="getStyles(index)"
          @click="dateSelected(index)"
        >
          <div v-if="getIsToday(index)" class="h600">
            <p
              class="text-center h900"
              :class="isUnavailable(index) ? 'line-through' : ''"
            >
              {{ getFormattedDate(index) }}
            </p>
          </div>

          <div v-else class="h600">
            <p class="text-center">
              {{ getFormattedDate(index)[0] }}
            </p>
            <p
              class="text-center h1000"
              :class="isUnavailable(index) ? 'line-through' : ''"
            >
              {{ getFormattedDate(index)[1] }}
            </p>
            <p class="text-center">
              {{ getFormattedDate(index)[2] }}
            </p>
          </div>
        </div>
        <div class="min-w-4 h-2 foobar"></div>
      </div>
    </div>
  </div>
</template>
